import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, Video, Upload, Heart, MessageCircle, Award, ChevronRight, ImageIcon, Star, Users, X, CheckCircle, FileVideo, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { mediaAPI } from '../../services/api';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GwinsArmy = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [mediaGallery, setMediaGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Upload Modal State
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    uploader_name: '',
    uploader_email: '',
    caption: '',
    team_name: '',
    tagged_players: []  // NEW: Array of player_ids
  });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [filePreviews, setFilePreviews] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  
  // Player tagging state
  const [availablePlayers, setAvailablePlayers] = useState([]);
  const [playerSearchQuery, setPlayerSearchQuery] = useState('');
  const [showPlayerDropdown, setShowPlayerDropdown] = useState(false);

  useEffect(() => {
    const fetchMedia = async () => {
      try {
        const data = await mediaAPI.getAll();
        if (data && data.length > 0) {
          const transformedMedia = data.map(item => {
            // Handle both url and file_path fields
            let mediaUrl = item.url || item.file_path || '';
            // Prepend API_URL for relative paths
            if (mediaUrl && (mediaUrl.startsWith('/api') || mediaUrl.startsWith('/uploads'))) {
              mediaUrl = `${API_URL}${mediaUrl}`;
            }
            return {
              id: item.media_id,
              type: item.type || 'image',
              url: mediaUrl,
              author: item.team_name || item.uploader_name || 'Community',
              team: item.team_name || 'Community',
              likes: item.likes || (item.liked_by ? item.liked_by.length : 0),
              comments: item.comment_count || 0,
              isSponsored: item.is_sponsored || false,
              caption: item.caption || '',
            };
          }).filter(item => item.url); // Filter out items without URLs
          setMediaGallery(transformedMedia);
        }
      } catch (error) {
        console.error('Error fetching media:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMedia();
  }, []);

  // Fetch players for tagging when modal opens
  useEffect(() => {
    const fetchPlayers = async () => {
      if (showUploadModal) {
        try {
          const response = await fetch(`${API_URL}/api/players/list/for-tagging`);
          const data = await response.json();
          setAvailablePlayers(data.players || []);
        } catch (error) {
          console.error('Error fetching players:', error);
        }
      }
    };
    fetchPlayers();
  }, [showUploadModal]);

  // Filter players based on search
  const filteredPlayers = availablePlayers.filter(player => 
    !uploadForm.tagged_players.includes(player.player_id) &&
    (player.name?.toLowerCase().includes(playerSearchQuery.toLowerCase()) ||
     player.nickname?.toLowerCase().includes(playerSearchQuery.toLowerCase()))
  );

  // Add player to tags
  const addPlayerTag = (player) => {
    setUploadForm(prev => ({
      ...prev,
      tagged_players: [...prev.tagged_players, player.player_id]
    }));
    setPlayerSearchQuery('');
    setShowPlayerDropdown(false);
  };

  // Remove player from tags
  const removePlayerTag = (playerId) => {
    setUploadForm(prev => ({
      ...prev,
      tagged_players: prev.tagged_players.filter(id => id !== playerId)
    }));
  };

  // Get player name by id
  const getPlayerName = (playerId) => {
    const player = availablePlayers.find(p => p.player_id === playerId);
    return player ? (player.nickname || player.name) : playerId;
  };

  const categories = [
    { id: 'all', label: 'All Media' },
    { id: 'nyaces', label: 'NY Aces' },
    { id: 'sponsored', label: 'Sponsored' },
    { id: 'community', label: 'Community' },
  ];

  const filteredMedia = mediaGallery.filter(item => {
    if (selectedCategory === 'all') return true;
    if (selectedCategory === 'nyaces') return item.team === 'NY Aces';
    if (selectedCategory === 'sponsored') return item.isSponsored;
    if (selectedCategory === 'community') return !item.isSponsored;
    return true;
  });

  const nyAcesCount = mediaGallery.filter(item => item.team === 'NY Aces').length;

  // Handle file selection
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    
    if (files.length > 10) {
      toast.error('Maximum 10 files allowed per upload');
      return;
    }
    
    // Validate files
    const validFiles = [];
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'video/mp4', 'video/quicktime', 'video/webm', 'video/x-msvideo'];
    
    files.forEach(file => {
      if (!allowedTypes.includes(file.type)) {
        toast.error(`${file.name}: Invalid file type`);
        return;
      }
      
      const isVideo = file.type.startsWith('video/');
      const maxSize = isVideo ? 50 * 1024 * 1024 : 5 * 1024 * 1024;
      
      if (file.size > maxSize) {
        toast.error(`${file.name}: File too large (max ${isVideo ? '50MB' : '5MB'})`);
        return;
      }
      
      validFiles.push(file);
    });
    
    setSelectedFiles(prev => [...prev, ...validFiles].slice(0, 10));
    
    // Generate previews
    validFiles.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        setFilePreviews(prev => [...prev, {
          name: file.name,
          url: e.target.result,
          type: file.type.startsWith('video/') ? 'video' : 'image',
          size: file.size
        }].slice(0, 10));
      };
      reader.readAsDataURL(file);
    });
  };

  // Remove a selected file
  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setFilePreviews(prev => prev.filter((_, i) => i !== index));
  };

  // Handle upload form submission
  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    
    if (!uploadForm.uploader_name.trim() || !uploadForm.uploader_email.trim()) {
      toast.error('Please enter your name and email');
      return;
    }
    
    if (selectedFiles.length === 0) {
      toast.error('Please select at least one file to upload');
      return;
    }
    
    setUploading(true);
    setUploadProgress(0);
    
    try {
      const formData = new FormData();
      formData.append('uploader_name', uploadForm.uploader_name);
      formData.append('uploader_email', uploadForm.uploader_email);
      formData.append('caption', uploadForm.caption);
      formData.append('team_name', uploadForm.team_name);
      formData.append('tagged_players', uploadForm.tagged_players.join(','));  // NEW: Send tagged players
      
      selectedFiles.forEach(file => {
        formData.append('files', file);
      });
      
      const response = await fetch(`${API_URL}/api/uploads/media/multiple`, {
        method: 'POST',
        body: formData
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Upload failed');
      }
      
      const data = await response.json();
      setUploadResults(data);
      setUploadSuccess(true);
      
      // Reset form
      setUploadForm({
        uploader_name: '',
        uploader_email: '',
        caption: '',
        team_name: '',
        tagged_players: []
      });
      setSelectedFiles([]);
      setFilePreviews([]);
      
      // Auto close after success
      setTimeout(() => {
        setShowUploadModal(false);
        setUploadSuccess(false);
        setUploadResults(null);
      }, 5000);
      
    } catch (error) {
      toast.error(error.message || 'Failed to upload media');
    } finally {
      setUploading(false);
    }
  };

  // Close modal and reset
  const closeModal = () => {
    setShowUploadModal(false);
    setUploadSuccess(false);
    setUploadResults(null);
    setSelectedFiles([]);
    setFilePreviews([]);
  };

  return (
    <section id="army" className="section-padding bg-[#0a0b0c]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="inline-block text-[#00d4aa] text-sm font-bold uppercase tracking-widest mb-4">
            User Generated Content
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase text-white mb-4">
            GWINS <span className="text-[#00d4aa]">Army</span>
          </h2>
          <p className="text-white/60 max-w-2xl mx-auto mb-8">
            Share your paintball moments. Tag your team, show off your gear,
            and become part of the GWINS legacy.
          </p>

          {/* Upload CTA */}
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              onClick={() => setShowUploadModal(true)}
              className="bg-[#00d4aa] hover:bg-[#33e0bb] text-[#0a0b0c] font-bold rounded-full"
              data-testid="upload-media-btn"
            >
              <Upload className="mr-2 h-4 w-4" />
              Upload Your Media
            </Button>
            <Button
              onClick={() => navigate('/community')}
              variant="outline"
              className="border-white/20 text-white hover:bg-white/5 rounded-full"
              data-testid="join-army-btn"
            >
              Join the GWINS Army
            </Button>
          </div>
        </div>

        {/* Featured Team Banner - NY Aces */}
        <div className="mb-8 p-6 rounded-2xl bg-gradient-to-r from-[#00d4aa]/10 via-[#131416] to-[#00d4aa]/10 border border-[#00d4aa]/20">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-xl bg-black/40 backdrop-blur-sm border border-[#00d4aa]/30 flex items-center justify-center overflow-hidden p-1">
                <img 
                  src="https://customer-assets.emergentagent.com/job_tactical-sports/artifacts/0c5nukgd_NY%20Aces%20Logo.jpg" 
                  alt="NY Aces"
                  className="w-full h-full object-contain"
                />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-white font-bold text-lg">NY Aces</h3>
                  <Badge className="bg-[#00d4aa] text-[#0a0b0c] text-xs">
                    <Star className="h-3 w-3 mr-1" />
                    Featured
                  </Badge>
                </div>
                <p className="text-white/60 text-sm">
                  {nyAcesCount} photos & videos • Official GWINS Sponsor
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              className="border-[#00d4aa]/30 text-[#00d4aa] hover:bg-[#00d4aa]/10 rounded-full"
              onClick={() => setSelectedCategory('nyaces')}
            >
              <Users className="mr-2 h-4 w-4" />
              View NY Aces Media
            </Button>
          </div>
        </div>

        {/* Category Tabs */}
        <div className="flex justify-center gap-2 mb-8 flex-wrap">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                selectedCategory === cat.id
                  ? 'bg-[#00d4aa] text-[#0a0b0c]'
                  : 'bg-white/5 text-white/60 hover:bg-white/10'
              }`}
            >
              {cat.label}
              {cat.id === 'nyaces' && (
                <span className="ml-2 bg-white/20 text-xs px-2 py-0.5 rounded-full">
                  {nyAcesCount}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Media Gallery */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredMedia.map((item) => (
            <div
              key={item.id}
              className={`group relative aspect-[4/3] rounded-xl overflow-hidden cursor-pointer ${
                item.team === 'NY Aces' ? 'ring-2 ring-[#00d4aa]/30' : ''
              }`}
            >
              <img
                src={item.url}
                alt={`Media by ${item.author}`}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
              
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a0b0c] via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              
              {item.team === 'NY Aces' && (
                <Badge className="absolute top-3 left-3 bg-[#00d4aa] text-[#0a0b0c] text-xs font-bold">
                  <Star className="h-3 w-3 mr-1" />
                  NY Aces
                </Badge>
              )}

              {item.isSponsored && item.team !== 'NY Aces' && (
                <Badge className="absolute top-3 left-3 bg-[#00d4aa] text-[#0a0b0c] text-xs">
                  <Award className="h-3 w-3 mr-1" />
                  Sponsored
                </Badge>
              )}

              <div className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/50 flex items-center justify-center">
                {item.type === 'video' ? (
                  <Video className="h-4 w-4 text-white" />
                ) : (
                  <ImageIcon className="h-4 w-4 text-white" />
                )}
              </div>

              <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
                <p className="text-white font-semibold">{item.author}</p>
                {item.caption && (
                  <p className="text-white/80 text-sm mt-1 line-clamp-2">{item.caption}</p>
                )}
                <div className="flex items-center gap-4 mt-2 text-white/60 text-sm">
                  <span className="flex items-center gap-1">
                    <Heart className="h-4 w-4" />
                    {item.likes}
                  </span>
                  <span className="flex items-center gap-1">
                    <MessageCircle className="h-4 w-4" />
                    {item.comments}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredMedia.length === 0 && (
          <div className="text-center py-16">
            <Camera className="h-12 w-12 text-white/20 mx-auto mb-4" />
            <p className="text-white/60">No media found in this category</p>
          </div>
        )}

        <div className="text-center mt-12">
          <Button
            onClick={() => navigate('/gallery')}
            variant="outline"
            className="border-white/20 text-white hover:bg-white/5 rounded-full"
            data-testid="view-gallery-btn"
          >
            View Full Gallery
            <ChevronRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm overflow-y-auto">
          <div className="w-full max-w-2xl bg-[#131416] border border-white/10 rounded-2xl overflow-hidden my-8">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <div>
                <h3 className="text-xl font-bold text-white">Upload Your Media</h3>
                <p className="text-white/60 text-sm mt-1">Share your paintball photos and videos</p>
              </div>
              <button 
                onClick={closeModal}
                className="text-white/60 hover:text-white p-2"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 max-h-[70vh] overflow-y-auto">
              {uploadSuccess ? (
                <div className="text-center py-8">
                  <CheckCircle className="w-16 h-16 text-[#00d4aa] mx-auto mb-4" />
                  <h4 className="text-xl font-bold text-white mb-2">Upload Successful!</h4>
                  <p className="text-white/60 mb-4">
                    {uploadResults?.total_uploaded || 0} file(s) uploaded successfully!
                  </p>
                  <p className="text-white/40 text-sm">
                    Your media will appear in the gallery once approved by our team.
                  </p>
                  {uploadResults?.errors?.length > 0 && (
                    <div className="mt-4 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                      <p className="text-red-400 text-sm font-medium mb-2">
                        {uploadResults.errors.length} file(s) failed:
                      </p>
                      {uploadResults.errors.map((err, i) => (
                        <p key={i} className="text-red-400/60 text-xs">{err.filename}: {err.error}</p>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <form onSubmit={handleUploadSubmit} className="space-y-4">
                  {/* User Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-white/60 text-sm mb-1">Your Name *</label>
                      <input
                        type="text"
                        value={uploadForm.uploader_name}
                        onChange={(e) => setUploadForm({...uploadForm, uploader_name: e.target.value})}
                        placeholder="John Doe"
                        className="w-full px-4 py-2 bg-[#1a1b1e] border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-[#00d4aa]"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-white/60 text-sm mb-1">Email *</label>
                      <input
                        type="email"
                        value={uploadForm.uploader_email}
                        onChange={(e) => setUploadForm({...uploadForm, uploader_email: e.target.value})}
                        placeholder="john@example.com"
                        className="w-full px-4 py-2 bg-[#1a1b1e] border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-[#00d4aa]"
                        required
                      />
                    </div>
                  </div>

                  {/* File Upload Area */}
                  <div>
                    <label className="block text-white/60 text-sm mb-1">Photos & Videos *</label>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept="image/jpeg,image/png,image/gif,image/webp,video/mp4,video/quicktime,video/webm,video/x-msvideo"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                    <div 
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full p-8 border-2 border-dashed border-white/20 rounded-lg text-center cursor-pointer hover:border-[#00d4aa]/50 transition-colors"
                    >
                      <Upload className="w-10 h-10 text-white/40 mx-auto mb-3" />
                      <p className="text-white/60 mb-1">Click to select files or drag and drop</p>
                      <p className="text-white/40 text-sm">Photos (JPG, PNG, GIF, WEBP) up to 5MB</p>
                      <p className="text-white/40 text-sm">Videos (MP4, MOV, WEBM, AVI) up to 50MB</p>
                      <p className="text-white/40 text-sm mt-1">Maximum 10 files per upload</p>
                    </div>
                  </div>

                  {/* File Previews */}
                  {filePreviews.length > 0 && (
                    <div>
                      <label className="block text-white/60 text-sm mb-2">
                        Selected Files ({filePreviews.length}/10)
                      </label>
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                        {filePreviews.map((preview, index) => (
                          <div key={index} className="relative group">
                            <div className="aspect-square rounded-lg overflow-hidden bg-[#1a1b1e]">
                              {preview.type === 'video' ? (
                                <div className="w-full h-full flex items-center justify-center">
                                  <FileVideo className="w-8 h-8 text-white/40" />
                                </div>
                              ) : (
                                <img 
                                  src={preview.url} 
                                  alt={preview.name}
                                  className="w-full h-full object-cover"
                                />
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => removeFile(index)}
                              className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X className="w-4 h-4 text-white" />
                            </button>
                            <p className="text-white/40 text-xs mt-1 truncate">{preview.name}</p>
                          </div>
                        ))}
                        
                        {/* Add More Button */}
                        {filePreviews.length < 10 && (
                          <div 
                            onClick={() => fileInputRef.current?.click()}
                            className="aspect-square rounded-lg border-2 border-dashed border-white/20 flex items-center justify-center cursor-pointer hover:border-[#00d4aa]/50 transition-colors"
                          >
                            <div className="text-center">
                              <Upload className="w-6 h-6 text-white/40 mx-auto" />
                              <p className="text-white/40 text-xs mt-1">Add More</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Optional Fields */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-white/60 text-sm mb-1">Team Name (optional)</label>
                      <input
                        type="text"
                        value={uploadForm.team_name}
                        onChange={(e) => setUploadForm({...uploadForm, team_name: e.target.value})}
                        placeholder="Your team name"
                        className="w-full px-4 py-2 bg-[#1a1b1e] border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-[#00d4aa]"
                      />
                    </div>
                    <div>
                      <label className="block text-white/60 text-sm mb-1">Caption (optional)</label>
                      <input
                        type="text"
                        value={uploadForm.caption}
                        onChange={(e) => setUploadForm({...uploadForm, caption: e.target.value})}
                        placeholder="Describe your media..."
                        className="w-full px-4 py-2 bg-[#1a1b1e] border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-[#00d4aa]"
                      />
                    </div>
                  </div>

                  {/* Player Tagging */}
                  <div className="relative">
                    <label className="block text-white/60 text-sm mb-1">Tag Players (optional)</label>
                    <div className="relative">
                      <input
                        type="text"
                        value={playerSearchQuery}
                        onChange={(e) => {
                          setPlayerSearchQuery(e.target.value);
                          setShowPlayerDropdown(true);
                        }}
                        onFocus={() => setShowPlayerDropdown(true)}
                        placeholder="Search players to tag..."
                        className="w-full px-4 py-2 bg-[#1a1b1e] border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-[#00d4aa]"
                      />
                      {/* Dropdown */}
                      {showPlayerDropdown && playerSearchQuery && filteredPlayers.length > 0 && (
                        <div className="absolute z-50 w-full mt-1 bg-[#1a1b1e] border border-white/10 rounded-lg max-h-48 overflow-y-auto">
                          {filteredPlayers.slice(0, 10).map(player => (
                            <button
                              key={player.player_id}
                              type="button"
                              onClick={() => addPlayerTag(player)}
                              className="w-full px-4 py-2 text-left text-white hover:bg-[#00d4aa]/20 flex items-center gap-2"
                            >
                              {player.profile_picture ? (
                                <img src={player.profile_picture} alt="" className="w-6 h-6 rounded-full object-cover" />
                              ) : (
                                <div className="w-6 h-6 rounded-full bg-[#00d4aa]/30 flex items-center justify-center text-xs text-[#00d4aa]">
                                  {player.name?.charAt(0) || '?'}
                                </div>
                              )}
                              <span>{player.nickname || player.name}</span>
                              {player.team_name && <span className="text-white/40 text-xs">• {player.team_name}</span>}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    {/* Tagged Players */}
                    {uploadForm.tagged_players.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {uploadForm.tagged_players.map(playerId => (
                          <span 
                            key={playerId}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-[#00d4aa]/20 text-[#00d4aa] rounded-full text-sm"
                          >
                            {getPlayerName(playerId)}
                            <button
                              type="button"
                              onClick={() => removePlayerTag(playerId)}
                              className="hover:text-white"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Info Box */}
                  <div className="bg-[#00d4aa]/10 border border-[#00d4aa]/20 rounded-lg p-4">
                    <p className="text-[#00d4aa] text-sm">
                      <strong>Note:</strong> All submissions are reviewed before appearing in the gallery. 
                      Please only upload paintball-related content that you have the rights to share.
                    </p>
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    className="w-full bg-[#00d4aa] hover:bg-[#33e0bb] text-[#0a0b0c] font-bold py-3"
                    disabled={uploading || selectedFiles.length === 0}
                  >
                    {uploading ? (
                      <span className="flex items-center justify-center">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Uploading {selectedFiles.length} file(s)...
                      </span>
                    ) : (
                      `Upload ${selectedFiles.length} File${selectedFiles.length !== 1 ? 's' : ''}`
                    )}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default GwinsArmy;
